import { keys } from '../keys';
import {
  createBillingConfig,
  PriceInterval,
  PriceModel,
  PriceType
} from './schema';

enum Feature {
  FreeModels = '4 Available Models to Choose From',
  FreePoses = '4 Available Poses to Choose From',
  FreeBackgrounds = '4 Available Backgrounds to Choose From',
  FreeCredits = '4 credits per month',
  ProModels = 'All available models to choose from',
  ProPoses = 'All available poses to choose from',
  ProBackgrounds = 'All available backgrounds to choose from',
  ProCredits = '30 credits per month + rollover',
  ProPlusCredits = '100 credits per month + rollover',
  EnterpriseModels = 'Custom Models',
  EnterprisePoses = 'Custom Poses',
  EnterpriseBackgrounds = 'Custom Backgrounds',
  EnterpriseCredits = '5000 credits per month + rollover',
  TopUpCredits = 'Additional credits available for purchase'
}

const currency = 'USD';

export const billingConfig = createBillingConfig({
  products: [
    {
      id: 'free',
      name: 'Free',
      description: 'Start for free.',
      label: 'Get started',
      isFree: true,
      features: [
        Feature.FreeModels,
        Feature.FreePoses,
        Feature.FreeBackgrounds,
        Feature.FreeCredits
      ],
      metadata: {
        monthlyCredits: 4,
        allowRollover: false
      },
      // Even if it is free, keep the plans and prices to display the interval and currency correctly
      plans: [
        {
          id: 'plan-free-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id: 'price-free-month-id', // a placebo ID is fine here
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              interval: PriceInterval.Month,
              cost: 0,
              currency
            }
          ]
        },
        {
          id: 'plan-free-year',
          displayIntervals: [PriceInterval.Year],
          prices: [
            {
              id: 'price-free-year-id', // a placebo ID is fine here
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 0,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'prod_Sys0BDxHdhENfT',
      name: 'Boutique',
      description: 'Best for boutiques, small e-commerce stores, and brands.',
      label: 'Get started',
      recommended: true,
      features: [
        Feature.ProModels,
        Feature.ProPoses,
        Feature.ProBackgrounds,
        Feature.ProCredits,
        Feature.TopUpCredits
      ],
      metadata: {
        monthlyCredits: 30,
        yearlyCredits: 360, // 12 months worth
        allowRollover: true
      },
      plans: [
        {
          id: 'plan-pro-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_BOUTIQUE_MONTH_ID ||
                'price-boutique-month-id',
              interval: PriceInterval.Month,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 30,
              currency
            }
          ]
        },
        {
          id: 'plan-pro-year',
          displayIntervals: [PriceInterval.Year],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_BOUTIQUE_YEAR_ID ||
                'price-boutique-year-id',
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 300,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'prod_T1AtagZQ4PbqXn',
      name: 'Marketplace',
      description:
        'Best for large e-commerce stores, marketplaces, and established brands.',
      label: 'Scale up',
      recommended: false,
      features: [
        Feature.ProModels,
        Feature.ProPoses,
        Feature.ProBackgrounds,
        Feature.ProPlusCredits,
        Feature.TopUpCredits
      ],
      metadata: {
        monthlyCredits: 100,
        yearlyCredits: 1200, // 12 months worth
        allowRollover: true
      },
      plans: [
        {
          id: 'plan-pro-plus-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_MARKETPLACE_MONTH_ID ||
                'price-marketplace-month-id',
              interval: PriceInterval.Month,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 100,
              currency
            }
          ]
        },
        {
          id: 'plan-pro-plus-year',
          displayIntervals: [PriceInterval.Year],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_MARKETPLACE_YEAR_ID ||
                'price-marketplace-year-id',
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 1000,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'Best for tailored requirements.',
      label: 'Contact us',
      isEnterprise: true,
      features: [
        Feature.ProModels,
        Feature.ProPoses,
        Feature.ProBackgrounds,
        Feature.EnterpriseModels,
        Feature.EnterprisePoses,
        Feature.EnterpriseBackgrounds,
        Feature.EnterpriseCredits,
        Feature.TopUpCredits
      ],
      metadata: {
        monthlyCredits: 5000,
        yearlyCredits: 60000, // 12 months worth
        allowRollover: true
      },
      // The idea is to keep the product and prices and use an admin panel to update the customer to enterprise.
      // For enterprise you can have multiple products, you just need to set hidden: true on the other enterprise products.
      plans: [
        {
          id: 'plan-enterprise-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id: 'price-enterprise-month-id', // a placebo ID is fine here
              interval: PriceInterval.Month,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 0,
              currency
            }
          ]
        },
        {
          id: 'plan-enterprise-year',
          displayIntervals: [PriceInterval.Year],
          prices: [
            {
              id: 'price-enterprise-year-id', // a placebo ID is fine here
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 0,
              currency
            }
          ]
        }
      ]
    },
    // Add top-up credit products
    {
      id: 'credits-10',
      name: '10 Credits',
      description: 'Top up with 10 additional credits',
      label: 'Buy Credits',
      hidden: true, // Don't show in main pricing table
      features: ['10 additional credits'],
      metadata: {
        creditAmount: 10
      },
      plans: [
        {
          id: 'plan-credits-10',
          displayIntervals: [],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_CREDITS_10_ID ||
                'price-credits-10-id',
              type: PriceType.OneTime,
              model: PriceModel.Flat,
              cost: 15,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'credits-35',
      name: '35 Credits',
      description: 'Top up with 35 additional credits',
      label: 'Buy Credits',
      hidden: true,
      features: ['35 additional credits'],
      metadata: {
        creditAmount: 35
      },
      plans: [
        {
          id: 'plan-credits-35',
          displayIntervals: [],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_CREDITS_35_ID ||
                'price-credits-35-id',
              type: PriceType.OneTime,
              model: PriceModel.Flat,
              cost: 40,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'credits-65',
      name: '65 Credits',
      description: 'Top up with 100 additional credits',
      label: 'Buy Credits',
      hidden: true,
      features: ['65 additional credits'],
      metadata: {
        creditAmount: 65
      },
      plans: [
        {
          id: 'plan-credits-65',
          displayIntervals: [],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_CREDITS_65_ID ||
                'price-credits-65-id',
              type: PriceType.OneTime,
              model: PriceModel.Flat,
              cost: 70,
              currency
            }
          ]
        }
      ]
    }
  ]
});

export const billingConfigDisplayIntervals = Array.from(
  new Set(
    billingConfig.products
      .filter((product) => !product.hidden)
      .flatMap((product) =>
        product.plans.flatMap((plan) => plan.displayIntervals)
      )
      .filter(Boolean)
  )
) as PriceInterval[];

// Export credit products separately for easy access
export const creditProducts = billingConfig.products.filter((p) =>
  p.id.startsWith('credits-')
);
